import {alpha} from '@mui/material/styles';
import {menuItemClasses} from '@mui/material/MenuItem';
import {gray, brand} from '../themePrimitives';

// Targets the universal `.Mui-selected` modifier rather than importing
// `pickerDayClasses`, which keeps this file independent of the day-cell's class
// name (renamed once already: pickersDayClasses → pickerDayClasses in v9).
const daySelectedStyles = {
    root: ({theme}) => ({
        fontSize: theme.typography.body1.fontSize,
        color: (theme.vars || theme).palette.grey[600],
        padding: theme.spacing(0.5),
        '&:hover': {
            backgroundColor: (theme.vars || theme).palette.action.hover
        },
        // The custom `color` above clobbers MUI's default disabled treatment, so
        // disabled days (e.g. past dates with `disablePast`) look enabled. Grey
        // them out explicitly.
        '&.Mui-disabled': {
            color: (theme.vars || theme).palette.text.disabled,
            opacity: 0.5,
            '&:hover': {backgroundColor: 'transparent'}
        },
        '&.Mui-selected': {
            backgroundColor: gray[700],
            color: '#fff',
            fontWeight: theme.typography.fontWeightMedium
        },
        '&:focus': {
            outline: `3px solid ${alpha(brand[500], 0.5)}`,
            outlineOffset: '2px',
            backgroundColor: 'transparent',
            '&.Mui-selected': {backgroundColor: gray[700], color: '#fff'}
        }
    })
};

// The same "this cell is not selectable" treatment the day cell gets above, for
// the month and year grids.
const disabledPickerCellStyles = ({theme}) => ({
    '&.Mui-disabled': {
        color: (theme.vars || theme).palette.text.disabled,
        opacity: 0.5,
        '&:hover': {backgroundColor: 'transparent'}
    }
});

export const datePickersCustomizations = {
    MuiPickersPopper: {
        styleOverrides: {
            paper: ({theme}) => ({
                marginTop: 4,
                borderRadius: theme.shape.borderRadius,
                border: `1px solid ${(theme.vars || theme).palette.divider}`,
                backgroundImage: 'none',
                background: 'hsl(0, 0%, 100%)',
                boxShadow:
          'hsla(215, 30%, 8%, 0.07) 0px 4px 16px 0px, hsla(215, 25%, 12%, 0.08) 0px 8px 16px -5px',
                [`& .${menuItemClasses.root}`]: {
                    borderRadius: 6,
                    margin: '0 6px'
                }
            })
        }
    },
    MuiPickersArrowSwitcher: {
        styleOverrides: {
            spacer: {width: 16},
            button: ({theme}) => ({
                backgroundColor: 'transparent',
                color: (theme.vars || theme).palette.grey[500]
            })
        }
    },
    MuiPickersCalendarHeader: {
        styleOverrides: {
            switchViewButton: {
                padding: 0,
                border: 'none'
            }
        }
    },
    // A month or year outside minDate/maxDate has to READ as unavailable. MUI's
    // own disabled colour for these two is `text.secondary` (unlike the day cell,
    // which uses `text.disabled`), and against this palette's `text.primary` that
    // is gray[600] vs gray[800] — a difference nobody can see, so a member's start
    // date silently rejects the month you just clicked. Match the day cell instead.
    //
    // Names matter here: `MuiPickersMonth` / `MuiPickersYear` (with their
    // `monthButton` / `yearButton` slots) were removed in x-date-pickers v9, so an
    // override under those keys is dead code that reads as live. The v9 names are
    // `MuiMonthCalendar` / `MuiYearCalendar`, both with a `button` slot — the same
    // rename that turned `MuiPickersDay` into `MuiPickerDay` below.
    //
    // Deliberately ONLY the disabled treatment: the pre-v9 block also restyled the
    // selected and hover states, and reviving that would change how every month and
    // year picker in the app looks, which is a separate decision from this one.
    MuiMonthCalendar: {
        styleOverrides: {
            button: disabledPickerCellStyles
        }
    },
    MuiYearCalendar: {
        styleOverrides: {
            button: disabledPickerCellStyles
        }
    },
    MuiPickerDay: {styleOverrides: daySelectedStyles},
    MuiPickersInputBase: {
        styleOverrides: {
            root: {
                background: '#fff'
            }
        }
    },
    MuiPickersOutlinedInput: {
        styleOverrides: {
            root: {
                background: '#fff'
            }
        }
    }
};
